import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Axios from 'axios';
import * as Routes from '@/modules/routes';
import QueryKeys from '@/utils/QueryKeys';

// A ModelResource on a program, as ModelResourceSerializer sends it (English text).
export interface ProgramResource {
  audience: string;
  auto_generated: boolean;
  html: string | null;
  icon: string | null;
  id: number;
  input_type: string;
  model_resource_template_id: number;
  name: string;
  position: number;
  published: boolean;
  title: string;
  url: string | null;
}

// A resource category (ModelResourceTemplate): its overrides lock the matching form fields.
export interface ResourceTemplate {
  audience_override: string | null;
  auto_generated: boolean;
  icon: string | null;
  id: number;
  input_type_override: string | null;
  name: string;
  title_override: string | null;
}

export interface ResourcePayload {
  audience: string;
  html?: string;
  input_type: string;
  model_resource_template_id: number;
  program_id: number;
  published: boolean;
  title: string;
  url?: string;
}

// Who the resources belong to. Programs and units both have them; a unit's still send the program.
export interface ResourceOwner {
  modelClass: 'Program' | 'Unit';
  modelId: number;
  programId: number;
}

export const resourcesQueryKey = ({ modelClass, modelId }: ResourceOwner) => (
  [QueryKeys.ModelResources, modelClass, modelId]
);

export const useProgramResources = (owner: ResourceOwner) => useQuery({
  queryFn: async () => {
    const { data } = await Axios.get<{ data: ProgramResource[] }>(
      Routes.plato_api_model_resources_path({ model_class: owner.modelClass, model_id: owner.modelId }),
    );
    return data.data;
  },
  queryKey: resourcesQueryKey(owner),
});

export const useResourceTemplates = (modelClass: ResourceOwner['modelClass']) => useQuery({
  queryFn: async () => {
    const { data } = await Axios.get<{ data: ResourceTemplate[] }>(
      Routes.plato_api_model_resource_templates_path({ model_class: modelClass }),
    );
    return data.data;
  },
  queryKey: [QueryKeys.ModelResourceTemplates, modelClass],
});

// Every write patches the cached list from the server's response, so the table re-renders
// without a refetch; the API returns the saved resource under `data`.
export const useSaveResource = (owner: ResourceOwner) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, resource }: { id?: number; resource: Partial<ResourcePayload> }) => (
      id
        ? Axios.put<{ data: ProgramResource }>(Routes.plato_api_model_resource_path(id), { model_resource: resource })
        : Axios.post<{ data: ProgramResource }>(
          Routes.plato_api_model_resources_path({ model_class: owner.modelClass, model_id: owner.modelId }),
          { model_resource: resource },
        )
    ).then(res => res.data.data),
    onSuccess: (saved, { id }) => {
      queryClient.setQueryData<ProgramResource[]>(resourcesQueryKey(owner), (list = []) => (
        id ? list.map(row => (row.id === id ? saved : row)) : [...list, saved]
      ));
    },
  });
};

export const useDeleteResource = (owner: ResourceOwner) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => Axios.delete(Routes.plato_api_model_resource_path(id)).then(() => id),
    onSuccess: (id) => {
      queryClient.setQueryData<ProgramResource[]>(resourcesQueryKey(owner), (list = []) => (
        list.filter(row => row.id !== id)
      ));
    },
  });
};

// Optimistic, like the lessons table: the cached order changes at once and the ids post; a failed
// save restores server truth and rethrows so the table skips its saved flash.
export const useReorderResources = (owner: ResourceOwner) => {
  const queryClient = useQueryClient();

  return (orderedRows: ProgramResource[]) => {
    queryClient.setQueryData<ProgramResource[]>(resourcesQueryKey(owner), orderedRows);

    return Axios
      .post(Routes.plato_api_reorder_model_resources_path(), { ids: orderedRows.map(row => row.id) })
      .catch((error) => {
        queryClient.invalidateQueries({ queryKey: resourcesQueryKey(owner) });
        throw error;
      });
  };
};

export const resourceErrorMessage = (error: unknown, fallback: string) => {
  const errors = Axios.isAxiosError(error) ? error.response?.data?.errors : undefined;
  return Array.isArray(errors) && errors.length > 0 ? errors.join(' ') : fallback;
};
